/* Instant Search – results + live filters drop down under the header search bar.
 * Uses Shopify's Section Rendering API + Search & Discovery filters.
 */
(() => {
  const root = document.querySelector('[data-instant-search]');
  if (!root) return;
  document.body.appendChild(root);

  const SECTION = 'instant-search-results';
  const DEBOUNCE = 220;
  const BAR_SELECTOR = 'form.tbs-hsearch, [data-instant-search-form]';
  const $ = (s) => root.querySelector(s);

  const el = {
    sort: $('[data-is-sort]'),
    filters: $('[data-is-filters]'),
    results: $('[data-is-results]'),
    empty: $('[data-is-empty]'),
    more: $('[data-is-more]'),
    status: $('[data-is-status]'),
    chips: $('[data-is-chips]'),
    viewall: $('[data-is-viewall]'),
  };

  const searchUrl = root.dataset.searchUrl || '/search';
  const allUrl = root.dataset.allUrl || '/collections/all';
  const rootUrl = (root.dataset.root || '/').replace(/\/$/, '');

  const state = { q: '', collection: '', sort: 'relevance', nextUrl: null, lastUrl: null };
  const cache = new Map();
  let controller = null;
  let timer = null;
  let activeForm = null;
  let activeInput = null;

  document.querySelectorAll(BAR_SELECTOR).forEach((form) => {
    const input = form.querySelector('input[name="q"]');
    if (!input) return;
    input.setAttribute('autocomplete', 'off');

    const activate = () => { activeForm = form; activeInput = input; state.q = input.value; };

    input.addEventListener('input', () => {
      activate();
      if (!state.q.trim()) { close(); return; }
      show();
      debounced(() => run());
    });
    input.addEventListener('focus', () => {
      activate();
      if (!state.q.trim()) return;
      show();
      if (state.lastUrl !== buildUrl()) run();
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { close(); input.blur(); }
      if (e.key === 'ArrowDown' && !root.hidden) {
        const first = el.results.querySelector('.is-card:not([hidden])');
        if (first) { e.preventDefault(); first.focus(); }
      }
    });
  });

  function place() {
    if (root.hidden || !activeForm) return;
    const r = activeForm.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const gutter = vw < 750 ? 8 : 16;
    const w = Math.min(1100, vw - gutter * 2);
    let left = r.left + r.width / 2 - w / 2;
    left = Math.max(gutter, Math.min(left, vw - w - gutter));
    const top = Math.max(r.bottom + 8, 8);
    root.style.top = `${top}px`;
    root.style.left = `${left}px`;
    root.style.width = `${w}px`;
    root.style.maxHeight = `${Math.max(260, window.innerHeight - top - 16)}px`;
  }
  function show() {
    if (root.hidden) { root.hidden = false; activeForm && activeForm.classList.add('is-active'); }
    place();
  }
  function close() {
    root.hidden = true;
    root.classList.remove('is-show-filters');
    document.querySelectorAll(BAR_SELECTOR).forEach((f) => f.classList.remove('is-active'));
  }
  window.addEventListener('resize', place);
  window.addEventListener('scroll', place, { passive: true });

  document.addEventListener('mousedown', (e) => {
    if (root.hidden) return;
    if (root.contains(e.target) || (activeForm && activeForm.contains(e.target))) return;
    close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !root.hidden) { close(); activeInput && activeInput.focus(); }
  });
  $('[data-is-close]').addEventListener('click', close);
  $('[data-is-toggle]').addEventListener('click', () => { root.classList.toggle('is-show-filters'); });

  el.results.addEventListener('keydown', (e) => {
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
    const cards = [...el.results.querySelectorAll('.is-card:not([hidden])')];
    const i = cards.indexOf(document.activeElement);
    if (i < 0) return;
    e.preventDefault();
    const cols = Math.max(1, getComputedStyle(el.results).gridTemplateColumns.split(' ').length);
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: cols, ArrowUp: -cols }[e.key];
    const next = i + step;
    if (next < 0) { activeInput && activeInput.focus(); return; }
    if (cards[next]) cards[next].focus();
  });

  function filterParams() {
    const p = new URLSearchParams();
    new FormData(el.filters).forEach((v, k) => {
      if (String(v).trim() !== '') p.append(k, v);
    });
    return p;
  }

  function buildUrl(page) {
    const p = filterParams();
    const q = state.q.trim();
    let base;
    if (q) {
      base = searchUrl;
      p.set('q', q);
      p.set('type', 'product');
      p.set('options[prefix]', 'last');
      p.set('options[unavailable_products]', 'last');
      if (state.sort !== 'relevance') p.set('sort_by', state.sort);
    } else {
      base = state.collection ? `${rootUrl}/collections/${state.collection}` : allUrl;
      p.set('sort_by', state.sort === 'relevance' ? 'best-selling' : state.sort);
    }
    if (page) p.set('page', page);
    p.set('section_id', SECTION);
    return `${base}?${p.toString()}`;
  }

  async function run({ append = false, url = null } = {}) {
    if (!state.q.trim()) return;
    const target = url || buildUrl();
    if (!append) state.lastUrl = target;
    if (controller) controller.abort();
    controller = new AbortController();
    root.classList.add('is-loading');
    try {
      let html = cache.get(target);
      if (!html) {
        const res = await fetch(target, { signal: controller.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        html = await res.text();
        if (cache.size > 60) cache.delete(cache.keys().next().value);
        cache.set(target, html);
      }
      render(html, append);
    } catch (err) {
      if (err.name !== 'AbortError') {
        el.status.textContent = 'Search failed – please try again.';
        console.error('[instant-search]', err);
      }
    } finally {
      root.classList.remove('is-loading');
    }
  }

  function render(html, append) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const meta = doc.querySelector('[data-is-meta]');
    const filtersSrc = doc.querySelector('[data-is-filters-src]');
    const resultsSrc = doc.querySelector('[data-is-results-src]');

    if (!append && filtersSrc) swapFilters(filtersSrc);

    const cards = resultsSrc ? [...resultsSrc.children] : [];
    if (append) el.results.append(...cards);
    else { el.results.replaceChildren(...cards); el.results.parentElement.scrollTop = 0; }

    state.nextUrl = meta && meta.dataset.next ? meta.dataset.next : null;
    applyClientCollection();
    renderChips();

    const total = meta ? Number(meta.dataset.total) : 0;
    updateCount(state.collection ? null : total);
    el.more.hidden = !state.nextUrl;

    const vp = filterParams();
    vp.set('q', state.q.trim());
    vp.set('type', 'product');
    vp.set('options[prefix]', 'last');
    el.viewall.href = `${searchUrl}?${vp.toString()}`;
    place();
  }

  function updateCount(total) {
    const visible = el.results.querySelectorAll('.is-card:not([hidden])').length;
    const n = total == null ? visible : total;
    el.status.textContent = `${n} product${n === 1 ? '' : 's'} for “${state.q.trim()}”`;
    el.empty.hidden = visible > 0;
    el.viewall.hidden = visible === 0;
  }

  function swapFilters(src) {
    const openState = {};
    el.filters.querySelectorAll('details[data-filter-key]').forEach((d) => (openState[d.dataset.filterKey] = d.open));
    const active = document.activeElement;
    const focusId = active && el.filters.contains(active) ? active.id : null;
    const caret = focusId && active.selectionStart != null ? active.selectionStart : null;

    el.filters.replaceChildren(...src.childNodes);
    el.filters.querySelectorAll('details[data-filter-key]').forEach((d) => {
      if (d.dataset.filterKey in openState) d.open = openState[d.dataset.filterKey];
    });

    const sel = el.filters.querySelector('[data-is-collection]');
    if (sel) {
      if (state.collection && ![...sel.options].some((o) => o.value === state.collection)) {
        sel.add(new Option(state.collection.replace(/-/g, ' '), state.collection));
      }
      sel.value = state.collection;
    }
    if (focusId) {
      const f = document.getElementById(focusId);
      if (f) { f.focus(); try { if (caret != null) f.setSelectionRange(caret, caret); } catch (_) {} }
    }
  }

  function applyClientCollection() {
    const c = state.collection;
    el.results.querySelectorAll('.is-card').forEach((card) => {
      card.hidden = !!c && !card.dataset.collections.includes(` ${c} `);
    });
  }

  function renderChips() {
    const chips = [];
    if (state.collection) {
      const sel = el.filters.querySelector('[data-is-collection]');
      const label = sel && sel.selectedOptions[0] ? sel.selectedOptions[0].text : state.collection;
      chips.push({ label: `Collection: ${label}`, clear: () => { state.collection = ''; } });
    }
    el.filters.querySelectorAll('input[type=checkbox]:checked').forEach((cb) => {
      const group = cb.closest('details').querySelector('summary').childNodes[0].textContent.trim();
      const label = cb.closest('label').querySelector('.is-label').textContent;
      chips.push({ label: `${group}: ${label}`, clear: () => { cb.checked = false; } });
    });
    const priceIns = el.filters.querySelectorAll('.is-price input');
    const [min, max] = [priceIns[0] && priceIns[0].value, priceIns[1] && priceIns[1].value];
    if (min || max) {
      chips.push({ label: `Price: ${min || '0'} – ${max || '∞'}`, clear: () => priceIns.forEach((i) => (i.value = '')) });
    }

    el.chips.replaceChildren(...chips.map((c) => {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'is-chip'; b.textContent = `${c.label} ✕`;
      b.addEventListener('click', () => { c.clear(); run(); });
      return b;
    }));
    if (chips.length > 1) {
      const all = document.createElement('button');
      all.type = 'button'; all.className = 'is-chip is-chip--clear'; all.textContent = 'Clear all';
      all.addEventListener('click', () => {
        state.collection = '';
        el.filters.querySelectorAll('input').forEach((i) => (i.type === 'checkbox' ? (i.checked = false) : (i.value = '')));
        run();
      });
      el.chips.append(all);
    }
  }

  const debounced = (fn, ms = DEBOUNCE) => { clearTimeout(timer); timer = setTimeout(fn, ms); };

  el.sort.addEventListener('change', () => { state.sort = el.sort.value; run(); });
  el.filters.addEventListener('change', (e) => {
    if (e.target.matches('[data-is-collection]')) {
      state.collection = e.target.value;
      applyClientCollection(); renderChips(); updateCount(state.collection ? null : undefined);
      if (!state.collection) run();
      return;
    }
    if (e.target.type === 'checkbox') run();
  });
  el.filters.addEventListener('input', (e) => {
    if (e.target.closest('.is-price')) debounced(() => run(), 450);
  });

  el.more.addEventListener('click', () => {
    if (!state.nextUrl) return;
    const u = new URL(state.nextUrl, location.origin);
    u.searchParams.set('section_id', SECTION);
    run({ append: true, url: u.pathname + u.search });
  });
})();
